// ProAccessPage — AYA PRO · Espace professionnels — REFONTE selon MAQUETTE (v5.24.0).
// Header sombre compact (AYA PRO · promesses) · recherche instantanée ·
// grille compacte de métiers (cartes douces, icône + nom + desc + chevron) ·
// clic = choix du métier → écran « Se connecter / Créer mon espace » · CTA final unique.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Plus, Search, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ACTIVITIES, normStr as _norm, SECTOR_KEY, findActivity } from "@/components/pro/proActivities";
import { AyaProLogo } from "@/components/pro/AyaProLogo";

const API = process.env.REACT_APP_BACKEND_URL;


// Micro-interactions (élévation douce au survol/toucher).
if (typeof document !== "undefined" && !document.getElementById("aya-pro-anim")) {
  const st = document.createElement("style");
  st.id = "aya-pro-anim";
  st.textContent = `
    .ayaProCard{transition:transform .18s ease, box-shadow .18s ease;cursor:pointer;border:none;text-align:left}
    .ayaProCard:hover,.ayaProCard:active{transform:translateY(-3px);box-shadow:0 8px 20px rgba(17,24,39,.1)}
    .ayaProCard:hover .ayaProIcon{transform:scale(1.08)}
    .ayaProIcon{transition:transform .18s ease;display:inline-block}
    @keyframes ayaProIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
    .ayaProIn{animation:ayaProIn .3s ease both}
  `;
  document.head.appendChild(st);
}

export default function ProAccessPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [showCabinets, setShowCabinets] = useState(false);
  const [cabinets, setCabinets] = useState(null);
  const [savedSector, setSavedSector] = useState(() => findActivity(localStorage.getItem(SECTOR_KEY)));
  const pick = (a) => { setShowCabinets(false); setPicked(a); localStorage.setItem(SECTOR_KEY, a.id); setSavedSector(a); };

  useEffect(() => {
    if (showCabinets && cabinets === null) {
      axios.get(`${API}/api/v3/cabinet`).then((r) => setCabinets(r.data.cabinets || [])).catch(() => setCabinets([]));
    }
  }, [showCabinets, cabinets]);

  const shown = useMemo(() => {
    const nq = _norm(q.trim());
    if (!nq) return ACTIVITIES;
    return ACTIVITIES.filter((a) => _norm(`${a.label} ${a.sub} ${a.keywords}`).includes(nq));
  }, [q]);

  const connect = (a) => {
    if (a.cabinets) { setShowCabinets(true); return; }
    if (a.login) { navigate(a.login); return; }
    toast.info("L'espace de connexion de ce secteur arrive bientôt. Vous pouvez déjà créer votre espace.");
  };

  // ── ÉCRAN 2 : métier choisi → Se connecter / Créer mon espace ──────────────
  if (picked) {
    const a = picked;
    return (
      <div style={S.page} data-testid="pro-access-page">
        <div style={{ ...S.wrap, paddingTop: 18 }}>
          <button onClick={() => { setPicked(null); setShowCabinets(false); }} style={S.backBtn} data-testid="pro-activity-back">
            <ArrowLeft size={14} /> Retour
          </button>
          <div className="ayaProIn" style={{ background: "#fff", borderRadius: 18, padding: "24px 20px", marginTop: 16, textAlign: "center" }}>
            <div style={{ width: 66, height: 66, borderRadius: 18, background: a.bg, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>{a.icon}</div>
            <h2 style={{ margin: "12px 0 2px", fontSize: 19, fontWeight: 900, color: "#111827" }} data-testid="pro-picked-label">{a.label}</h2>
            <div style={{ color: "#6B7280", fontSize: 12.5 }}>{a.sub}</div>
            <p style={{ fontSize: 14, fontWeight: 800, color: "#111827", margin: "20px 0 12px" }}>
              Vous avez déjà {a.cabinets ? "un cabinet" : "un espace"} sur AYA ?
            </p>
            <div style={{ display: "grid", gap: 9, maxWidth: 340, margin: "0 auto" }}>
              <button onClick={() => connect(a)} data-testid="pro-picked-login"
                style={{ padding: "13px 16px", borderRadius: 12, border: "none", background: "#0E8A4F", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                Se connecter
              </button>
              <button onClick={() => navigate(`/inscription-pro?activity=${a.id}`)} data-testid="pro-picked-signup"
                style={{ padding: "12px 16px", borderRadius: 12, border: "1.5px solid #0E8A4F", background: "#fff", color: "#0E8A4F", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
                Créer mon espace
              </button>
            </div>
            {a.cabinets && showCabinets && (
              <div style={{ marginTop: 16, display: "grid", gap: 7, textAlign: "left" }} data-testid="pro-cabinet-list">
                {cabinets === null && <div style={{ color: "#6B7280", fontSize: 12 }}>Chargement des cabinets…</div>}
                {cabinets?.map((c) => (
                  <button key={c.slug} onClick={() => navigate(`/cabinet/${c.slug}/admin`)} data-testid={`pro-cabinet-${c.slug}`}
                    style={{ display: "flex", gap: 10, alignItems: "center", padding: "11px 13px", borderRadius: 12, border: "1px solid #E5E7EB", background: "#FAFAF9", cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 17 }}>⚖️</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#111827" }}>{c.name}{c.is_demo ? " (DEMO)" : ""}</span>
                      <span style={{ display: "block", fontSize: 11, color: "#6B7280" }}>{c.tagline}{c.commune ? ` · ${c.commune}` : ""}</span>
                    </span>
                    <span style={{ color: "#0E8A4F", fontSize: 12, fontWeight: 800, whiteSpace: "nowrap" }}>Espace →</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── ÉCRAN 1 : header + promesses + recherche + grille + CTA ────────────────
  return (
    <div style={S.page} data-testid="pro-access-page">
      {/* HEADER sombre compact */}
      <div style={S.hero}>
        <img src="/aya/pro/hero_pro.jpg" alt="" style={S.heroImg} />
        <div style={S.heroFade} />
        <div style={{ ...S.wrap, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 14 }}>
            <button onClick={() => navigate(-1)} style={S.backBtn} data-testid="pro-access-back"><ArrowLeft size={14} /> Retour</button>
            <AyaProLogo variant="header" />
            <span style={{ width: 70 }} />
          </div>
          <div style={{ maxWidth: 380, padding: "18px 0 16px" }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 3, color: "#F5B301" }}>ESPACE PROFESSIONNELS</div>
            <h1 style={{ margin: "8px 0 0", fontSize: 25, lineHeight: 1.15, fontWeight: 900, color: "#fff" }}>
              Développez votre activité avec <span style={{ color: "#34D399" }}>AYA</span>
            </h1>
            <p style={{ margin: "9px 0 0", fontSize: 13, lineHeight: 1.5, color: "#A7E3C4" }}>
              Rejoignez une communauté de professionnels et soyez visible auprès de milliers de clients.
            </p>
          </div>
          {/* 3 promesses compactes */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingBottom: 20 }} data-testid="pro-promises">
            {[[Users, "Plus de clients", "Via AYA Search"], [BarChart3, "Des outils simples", "Pour gérer votre activité"], [ShieldCheck, "Une plateforme fiable", "Vérifiée par AYA"]].map(([Ic, t, s]) => (
              <div key={t} style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 150 }}>
                <span style={{ width: 32, height: 32, borderRadius: "50%", border: "1.5px solid rgba(245,179,1,0.6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Ic size={14} color="#F5B301" />
                </span>
                <span>
                  <span style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#fff" }}>{t}</span>
                  <span style={{ display: "block", fontSize: 10.5, color: "rgba(255,255,255,0.65)" }}>{s}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PANNEAU BLANC : choix du métier */}
      <div style={{ background: "#fff", borderRadius: "22px 22px 0 0", marginTop: -14, position: "relative", minHeight: "55vh", paddingBottom: 34 }}>
        <div style={{ ...S.wrap, paddingTop: 20 }}>
          {/* Secteur mémorisé : raccourci direct vers son espace */}
          {savedSector && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: "#E8F8EF", border: "1px solid rgba(14,138,79,0.2)", borderRadius: 14, padding: "10px 13px", marginBottom: 16 }} data-testid="pro-saved-sector">
              <span style={{ fontSize: 20 }}>{savedSector.icon}</span>
              <span style={{ flex: 1, minWidth: 140, fontSize: 12, fontWeight: 800, color: "#065F46" }}>👋 Content de vous revoir — {savedSector.label}</span>
              <button onClick={() => pick(savedSector)} data-testid="pro-saved-sector-go"
                style={{ padding: "8px 13px", borderRadius: 999, border: "none", background: "#0E8A4F", color: "#fff", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>Accéder à mon espace →</button>
              <button onClick={() => { localStorage.removeItem(SECTOR_KEY); setSavedSector(null); }} data-testid="pro-saved-sector-clear"
                style={{ background: "none", border: "none", color: "#6B7280", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Changer</button>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ minWidth: 200 }}>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 900, color: "#111827" }}>Choisissez votre activité</h2>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6B7280" }}>Sélectionnez votre secteur pour vous connecter à votre espace ou vous inscrire.</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", borderRadius: 999, border: "1px solid #E5E7EB", background: "#F9FAFB", minWidth: 200, flex: "1 1 200px", maxWidth: 290 }}>
              <Search size={14} color="#9CA3AF" style={{ flexShrink: 0 }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un métier…" data-testid="pro-search-input"
                style={{ border: "none", outline: "none", background: "none", fontSize: 12.5, color: "#111827", width: "100%" }} />
            </div>
          </div>

          {/* GRILLE compacte */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, marginTop: 16 }} data-testid="pro-activity-grid">
            {shown.map((a) => (
              <button key={a.id} className="ayaProCard ayaProIn" onClick={() => pick(a)} data-testid={`pro-activity-${a.id}`}
                style={{ background: a.bg, borderRadius: 16, padding: "16px 13px 13px", position: "relative", minHeight: 150, display: "flex", flexDirection: "column" }}>
                <span className="ayaProIcon" style={{ fontSize: 34, lineHeight: 1 }}>{a.icon}</span>
                <span style={{ fontSize: 13.5, fontWeight: 900, color: "#111827", lineHeight: 1.2, margin: "10px 0 3px" }}>{a.label}</span>
                <span style={{ fontSize: 10.5, color: "#6B7280", lineHeight: 1.35, paddingRight: 26 }}>{a.sub}</span>
                <span style={{ position: "absolute", right: 10, bottom: 10, width: 26, height: 26, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#111827" }}>›</span>
              </button>
            ))}
            {shown.length === 0 && (
              <div style={{ gridColumn: "1 / -1", color: "#6B7280", fontSize: 12.5, padding: "18px 4px" }} data-testid="pro-search-empty">
                Aucun métier trouvé pour « {q} ». <button onClick={() => navigate("/inscription-pro")} style={{ background: "none", border: "none", color: "#0E8A4F", fontWeight: 800, cursor: "pointer", fontSize: 12.5, padding: 0 }}>Référencez votre activité →</button>
              </div>
            )}
          </div>

          {/* CTA FINAL unique */}
          <div style={{ display: "flex", gap: 13, alignItems: "center", flexWrap: "wrap", background: "#FDF3DE", borderRadius: 18, padding: "16px 17px", marginTop: 18 }} data-testid="pro-final-cta">
            <span style={{ fontSize: 34, flexShrink: 0 }}>🏪</span>
            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 2, color: "#C2410C" }}>PAS ENCORE INSCRIT ?</div>
              <div style={{ fontSize: 15.5, fontWeight: 900, color: "#111827", margin: "3px 0 2px" }}>Référencez votre activité sur AYA</div>
              <div style={{ fontSize: 11.5, color: "#6B7280", lineHeight: 1.4 }}>C'est simple, rapide et vous permet d'être visible auprès de milliers de clients en Côte d'Ivoire.</div>
            </div>
            <button onClick={() => navigate("/inscription-pro")} data-testid="pro-cta-signup"
              style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "13px 20px", borderRadius: 13, border: "none", background: "#F97316", color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
              <Plus size={15} /> S'inscrire maintenant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "#06281A", fontFamily: "'Manrope', system-ui, sans-serif" },
  wrap: { maxWidth: 820, margin: "0 auto", padding: "0 16px", boxSizing: "border-box" },
  hero: { position: "relative", overflow: "hidden", background: "radial-gradient(130% 120% at 20% 0%, #0B4A2F 0%, #06281A 65%)" },
  heroImg: { position: "absolute", right: -30, top: 0, height: "105%", width: "auto", maxWidth: "58%", objectFit: "cover", objectPosition: "left center", opacity: 0.9, maskImage: "linear-gradient(90deg, transparent 0%, #000 35%)", WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 35%)" },
  heroFade: { position: "absolute", inset: 0, background: "linear-gradient(90deg, #06281Acc 30%, transparent 70%)" },
  backBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.22)", color: "#fff", borderRadius: 999, padding: "8px 15px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
};
